import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import type { Segment } from '@/lib/noteSegments'
import { segmentsToText } from '@/lib/noteSegments'
import {
  MEDIA_TOKEN_REGEX,
  makeMediaToken,
  mediaKindFromKey,
  thumbKeyFor,
} from '@/lib/noteMedia'
import type { User } from '@/data/users/userSchemas'
import { getUserColour } from '@/lib/userColour'
import { GOVUK_COLOURS } from './govuk/colours'

interface NoteEditorProps {
  /** The last-saved segments — used to render colours on load/save */
  segments: Segment[]
  users: User[]
  currentUser: string
  /** Presigned S3 URLs keyed by media key (and video thumb key), for inline [media:key] tokens */
  mediaUrls: Record<string, string>
  onChange: (text: string) => void
  /** Open the full-size modal viewer for a media key */
  onExpandMedia: (key: string) => void
  /** Files pasted or dropped into the editor — route them through the upload pipeline */
  onMediaFiles: (files: File[]) => void
}

export interface NoteEditorHandle {
  /** Append media tokens (with a line break around each) at the end of the note */
  insertMedia: (
    items: { key: string; url: string; thumbKey?: string; thumbUrl?: string }[],
  ) => void
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Zero-width space "caret anchors" placed around widgets. Chrome cannot put a
// caret between two adjacent contenteditable=false elements, so without these
// a run of images becomes uneditable — no typing or deleting anywhere near it.
// readText strips them, so they never reach the saved note.
const ZWSP = '\u200B'

const IMG_STYLE =
  'max-width:100%;max-height:300px;border-radius:4px;display:inline-block;vertical-align:middle;cursor:zoom-in;'
const VIDEO_STYLE =
  'max-width:100%;max-height:320px;border-radius:4px;display:block;background:#000;'
const CHIP_STYLE =
  'display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border:2px solid ' +
  GOVUK_COLOURS.midGrey +
  ';border-radius:4px;background:#f3f2f1;color:#1a1a1a;font-size:15px;font-family:inherit;cursor:pointer;'
const SMALL_BTN_STYLE =
  'border:none;background:none;padding:2px 6px;font-size:13px;color:#505a5f;cursor:pointer;text-decoration:underline;font-family:inherit;'
const REMOVE_BTN_STYLE =
  'position:absolute;top:6px;right:6px;width:24px;height:24px;border-radius:50%;border:none;background:rgba(0,0,0,0.55);color:#ffffff;font-size:15px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;'

function removeButtonHTML(key: string): string {
  return `<button type="button" data-media-action="remove" data-media-key="${escapeAttr(key)}" title="Remove from note" aria-label="Remove from note" style="${REMOVE_BTN_STYLE}">×</button>`
}

function videoChipHTML(key: string): string {
  return `<button type="button" data-media-action="reveal" data-media-key="${escapeAttr(key)}" style="${CHIP_STYLE}">▶&nbsp;Show video</button>`
}

/** Collapsed video with a poster thumbnail and a play overlay */
function videoThumbHTML(key: string, thumbUrl: string): string {
  const keyAttr = escapeAttr(key)
  return (
    `<span data-media-action="reveal" data-media-key="${keyAttr}" style="position:relative;display:inline-block;max-width:100%;cursor:pointer;line-height:0;">` +
    `<img data-media-thumb="${keyAttr}" src="${escapeAttr(thumbUrl)}" alt="" style="max-width:100%;max-height:240px;border-radius:4px;display:block;">` +
    `<span style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;">` +
    `<span style="width:44px;height:44px;border-radius:50%;background:rgba(0,0,0,0.55);color:#ffffff;display:flex;align-items:center;justify-content:center;font-size:17px;padding-left:4px;">▶</span>` +
    `</span>` +
    `</span>`
  )
}

function mediaWidgetHTML(key: string, urls: Record<string, string>): string {
  const kind = mediaKindFromKey(key)
  const url = urls[key]
  const token = escapeAttr(makeMediaToken(key))
  const keyAttr = escapeAttr(key)
  const shell = (inner: string) =>
    `<span class="note-media" contenteditable="false" data-media-token="${token}" data-media-key="${keyAttr}" data-media-kind="${kind}" style="position:relative;display:inline-block;max-width:100%;margin:4px 0;">${inner}</span>`

  if (!url) {
    return shell(
      `<span data-media-loading="true" style="${CHIP_STYLE}cursor:default;">Loading ${kind}…</span>`,
    )
  }
  if (kind === 'image') {
    return shell(
      `<img src="${escapeAttr(url)}" alt="" data-media-action="expand" data-media-key="${keyAttr}" style="${IMG_STYLE}">` +
        removeButtonHTML(key),
    )
  }
  // Videos start collapsed — poster thumbnail when available, chip otherwise
  const thumbUrl = urls[thumbKeyFor(key)]
  return shell(
    (thumbUrl ? videoThumbHTML(key, thumbUrl) : videoChipHTML(key)) +
      removeButtonHTML(key),
  )
}

// ── Smart deletion around caret anchors ──────────────────────────────────────
// Backspace/Delete next to a media widget first hits an invisible ZWSP anchor,
// which feels like a dead keypress (and between two widgets there can be
// several in a row). When the browser is about to delete only anchors, we take
// over and delete the whole run plus the adjacent widget/character in one go.

type DeletableAtom = { text: Text; index: number } | { element: HTMLElement }

function isAtomElement(n: Node): boolean {
  if (n.nodeType !== Node.ELEMENT_NODE) return false
  const el = n as HTMLElement
  return el.tagName === 'BR' || !!el.dataset.mediaToken
}

function deepestEdge(n: Node, edge: 'first' | 'last'): Node {
  let cur = n
  while (
    cur.nodeType === Node.ELEMENT_NODE &&
    !isAtomElement(cur) &&
    (edge === 'last' ? cur.lastChild : cur.firstChild)
  ) {
    cur = edge === 'last' ? cur.lastChild! : cur.firstChild!
  }
  return cur
}

function leafToAtom(
  root: HTMLElement,
  leaf: Node,
  backward: boolean,
): DeletableAtom | null {
  if (leaf.nodeType === Node.TEXT_NODE) {
    const t = leaf as Text
    if (t.length === 0) return siblingAtom(root, t, backward)
    return { text: t, index: backward ? t.length - 1 : 0 }
  }
  return { element: leaf as HTMLElement }
}

/** The atom just before/after `node` itself (used to skip empty text nodes) */
function siblingAtom(
  root: HTMLElement,
  node: Node,
  backward: boolean,
): DeletableAtom | null {
  let cur: Node | null = node
  while (cur && cur !== root && !(backward ? cur.previousSibling : cur.nextSibling)) {
    cur = cur.parentNode
  }
  if (!cur || cur === root) return null
  const sibling = backward ? cur.previousSibling! : cur.nextSibling!
  return leafToAtom(root, deepestEdge(sibling, backward ? 'last' : 'first'), backward)
}

/** The atom (character or BR/widget element) adjacent to the caret */
function atomAtCaret(
  root: HTMLElement,
  node: Node,
  offset: number,
  backward: boolean,
): DeletableAtom | null {
  if (node.nodeType === Node.TEXT_NODE) {
    const t = node as Text
    if (backward && offset > 0) return { text: t, index: offset - 1 }
    if (!backward && offset < t.length) return { text: t, index: offset }
    return siblingAtom(root, t, backward)
  }
  const childIndex = backward ? offset - 1 : offset
  if (childIndex >= 0 && childIndex < node.childNodes.length) {
    return leafToAtom(
      root,
      deepestEdge(node.childNodes[childIndex], backward ? 'last' : 'first'),
      backward,
    )
  }
  return siblingAtom(root, node, backward)
}

/**
 * If the next thing the caret would delete is only ZWSP anchor(s), delete the
 * whole anchor run plus the adjacent widget/character/line-break in one
 * gesture. Returns false when no anchors are in the way (default behaviour).
 */
function smartDelete(root: HTMLElement, backward: boolean): boolean {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return false
  const { anchorNode } = sel
  if (!anchorNode || !root.contains(anchorNode)) return false

  const zwsps: { text: Text; index: number }[] = []
  let node: Node = anchorNode
  let offset = sel.anchorOffset
  let final: DeletableAtom | null = null
  for (;;) {
    const atom = atomAtCaret(root, node, offset, backward)
    if (!atom) break
    if ('text' in atom && atom.text.data[atom.index] === ZWSP) {
      zwsps.push(atom)
      node = atom.text
      offset = backward ? atom.index : atom.index + 1
      continue
    }
    final = atom
    break
  }
  if (zwsps.length === 0) return false

  // caret lands where the deleted content was
  const marker = document.createTextNode('')
  if (final && 'element' in final) {
    final.element.replaceWith(marker)
  } else if (final) {
    final.text.deleteData(final.index, 1)
  }
  // delete anchor chars per text node, highest index first so offsets hold
  const byNode = new Map<Text, number[]>()
  for (const z of zwsps) {
    const arr = byNode.get(z.text) ?? []
    arr.push(z.index)
    byNode.set(z.text, arr)
  }
  for (const [text, indexes] of byNode) {
    for (const i of indexes.sort((a, b) => b - a)) text.deleteData(i, 1)
  }
  const caretAt: [Node, number] =
    final && 'element' in final
      ? [marker, 0]
      : final
        ? [final.text, final.index]
        : [marker, 0]
  if (!final) {
    // nothing beyond the anchors (edge of note) — keep caret where it was
    const last = zwsps[zwsps.length - 1]
    caretAt[0] = last.text
    caretAt[1] = Math.min(last.index, last.text.length)
  }
  sel.collapse(caretAt[0], caretAt[1])
  return true
}

/**
 * Thumbnail URLs are presigned without checking existence (older videos have
 * no thumb object) — swap any thumbnail that fails to load for the plain chip.
 */
function wireThumbFallbacks(root: HTMLElement) {
  for (const img of Array.from(
    root.querySelectorAll<HTMLImageElement>('img[data-media-thumb]'),
  )) {
    const key = img.dataset.mediaThumb ?? ''
    const swap = () => {
      const reveal = img.closest<HTMLElement>('[data-media-action="reveal"]')
      if (reveal) reveal.outerHTML = videoChipHTML(key)
    }
    if (img.complete && img.naturalWidth === 0) swap()
    else img.addEventListener('error', swap, { once: true })
  }
}

/** Swap a collapsed video chip/thumbnail for a small inline player (plus expand/hide controls) */
function revealVideo(widget: HTMLElement, url: string) {
  const key = widget.dataset.mediaKey ?? ''
  widget.innerHTML =
    `<video controls autoplay playsinline src="${escapeAttr(url)}" style="${VIDEO_STYLE}"></video>` +
    `<span style="display:flex;gap:8px;margin-top:2px;">` +
    `<button type="button" data-media-action="expand" data-media-key="${escapeAttr(key)}" style="${SMALL_BTN_STYLE}">⤢ Bigger view</button>` +
    `<button type="button" data-media-action="collapse" data-media-key="${escapeAttr(key)}" style="${SMALL_BTN_STYLE}">Hide video</button>` +
    `<button type="button" data-media-action="remove" data-media-key="${escapeAttr(key)}" style="${SMALL_BTN_STYLE}color:#d4351c;">Remove</button>` +
    `</span>`
}

function collapseVideo(widget: HTMLElement, urls: Record<string, string>) {
  const key = widget.dataset.mediaKey ?? ''
  const thumbUrl = urls[thumbKeyFor(key)]
  widget.innerHTML = thumbUrl ? videoThumbHTML(key, thumbUrl) : videoChipHTML(key)
  wireThumbFallbacks(widget)
}

/**
 * Render segments to HTML. Media tokens can straddle segment boundaries after a
 * diff, so we flatten to per-character authors, find tokens on the combined
 * text, and emit media widgets for tokens / coloured spans for everything else.
 */
function segmentsToHTML(
  segments: Segment[],
  users: User[],
  currentUser: string,
  mediaUrls: Record<string, string>,
): string {
  const text = segmentsToText(segments)
  const charAuthors: string[] = []
  for (const seg of segments) {
    // index loop (not for-of): positions must match UTF-16 indices used below
    for (let i = 0; i < seg.text.length; i++) charAuthors.push(seg.author)
  }

  const colourFor = (author: string) =>
    author === currentUser
      ? '#1a1a1a'
      : getUserColour(author, users, currentUser)

  const renderTextRun = (from: number, to: number): string => {
    let html = ''
    let runStart = from
    for (let i = from + 1; i <= to; i++) {
      if (i === to || charAuthors[i] !== charAuthors[runStart]) {
        const escaped = escapeHtml(text.slice(runStart, i)).replace(
          /\n/g,
          '<br>',
        )
        html += `<span style="color:${colourFor(charAuthors[runStart] ?? '')}">${escaped}</span>`
        runStart = i
      }
    }
    return html
  }

  let html = ''
  let pos = 0
  for (const match of text.matchAll(MEDIA_TOKEN_REGEX)) {
    const start = match.index
    if (start > pos) html += renderTextRun(pos, start)
    html += ZWSP + mediaWidgetHTML(match[1], mediaUrls) + ZWSP
    pos = start + match[0].length
  }
  if (pos < text.length) html += renderTextRun(pos, text.length)
  return html
}

/** Read plain text from the contentEditable, treating <br> as \n and media widgets as their token */
function readText(el: HTMLElement): string {
  let text = ''
  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? ''
      return
    }
    const element = node as HTMLElement
    if (element.tagName === 'BR') {
      text += '\n'
      return
    }
    if (element.dataset.mediaToken) {
      text += element.dataset.mediaToken
      return
    }
    for (const child of Array.from(node.childNodes)) walk(child)
  }
  walk(el)
  // drop the invisible caret anchors placed around media widgets
  text = text.replace(/\u200B/g, '')
  // contentEditable sometimes appends a trailing \n
  return text.endsWith('\n') ? text.slice(0, -1) : text
}

function mediaFilesFrom(list: FileList | null | undefined): File[] {
  if (!list) return []
  return Array.from(list).filter(
    (f) => f.type.startsWith('image/') || f.type.startsWith('video/'),
  )
}

export const NoteEditor = forwardRef<NoteEditorHandle, NoteEditorProps>(
  function NoteEditor(
    {
      segments,
      users,
      currentUser,
      mediaUrls,
      onChange,
      onExpandMedia,
      onMediaFiles,
    },
    ref,
  ) {
    const divRef = useRef<HTMLDivElement>(null)
    const prevUsersRef = useRef(users)
    const prevMediaUrlsRef = useRef(mediaUrls)
    const [displaySegments, setDisplaySegments] = useState<Segment[]>(segments)
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    // Native listener (not React's onBeforeInput) so we get InputEvent.inputType
    // on both physical and mobile virtual keyboards
    useEffect(() => {
      const el = divRef.current
      if (!el) return
      function handleBeforeInput(e: Event) {
        const input = e as InputEvent
        if (
          input.inputType !== 'deleteContentBackward' &&
          input.inputType !== 'deleteContentForward'
        ) {
          return
        }
        if (smartDelete(el!, input.inputType === 'deleteContentBackward')) {
          input.preventDefault()
          onChangeRef.current(readText(el!))
        }
      }
      el.addEventListener('beforeinput', handleBeforeInput)
      return () => el.removeEventListener('beforeinput', handleBeforeInput)
    }, [])

    // Re-render colours when segments change (note load/switch) or when users
    // load for the first time. During typing we leave the DOM alone so undo
    // history stays intact.
    useEffect(() => {
      if (!divRef.current) return
      setDisplaySegments(segments)
      const newText = segmentsToText(segments)
      const currentText = readText(divRef.current)
      const usersChanged = users !== prevUsersRef.current
      prevUsersRef.current = users
      if (newText !== currentText || usersChanged) {
        divRef.current.innerHTML = segmentsToHTML(
          segments,
          users,
          currentUser,
          mediaUrls,
        )
        wireThumbFallbacks(divRef.current)
      }
    }, [segments, users, currentUser]) // eslint-disable-line react-hooks/exhaustive-deps

    // When presigned URLs arrive, swap "Loading…" chips for real media in place —
    // a full innerHTML rebuild here would wipe unsaved edits and inserted widgets
    useEffect(() => {
      const el = divRef.current
      if (!el || mediaUrls === prevMediaUrlsRef.current) return
      prevMediaUrlsRef.current = mediaUrls
      for (const widget of Array.from(
        el.querySelectorAll<HTMLElement>('[data-media-token]'),
      )) {
        const key = widget.dataset.mediaKey ?? ''
        const url = mediaUrls[key]
        if (!url || !widget.querySelector('[data-media-loading]')) continue
        const wrapper = document.createElement('span')
        wrapper.innerHTML = mediaWidgetHTML(key, mediaUrls)
        if (wrapper.firstElementChild) {
          widget.replaceWith(wrapper.firstElementChild)
          wireThumbFallbacks(el)
        }
      }
    }, [mediaUrls])

    useImperativeHandle(ref, () => ({
      insertMedia(items) {
        const el = divRef.current
        if (!el) return
        for (const item of items) {
          const urls: Record<string, string> = { [item.key]: item.url }
          if (item.thumbKey && item.thumbUrl) urls[item.thumbKey] = item.thumbUrl
          const wrapper = document.createElement('span')
          wrapper.innerHTML = mediaWidgetHTML(item.key, urls)
          const widget = wrapper.firstElementChild
          if (!widget) continue
          el.appendChild(document.createElement('br'))
          el.appendChild(document.createTextNode(ZWSP))
          el.appendChild(widget)
          el.appendChild(document.createTextNode(ZWSP))
          el.appendChild(document.createElement('br'))
        }
        wireThumbFallbacks(el)
        onChange(readText(el))
      },
    }))

    function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      if (e.key !== 'Enter' || !divRef.current) return
      e.preventDefault()

      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return
      const range = sel.getRangeAt(0)
      range.deleteContents()

      const br = document.createElement('br')
      range.insertNode(br)
      range.setStartAfter(br)
      range.collapse(true)

      // A trailing <br> won't show the cursor on a new line unless there's
      // something after it — add a sentinel <br> if we're at the end
      const atEnd =
        !br.nextSibling ||
        (br.nextSibling.nodeType === Node.TEXT_NODE &&
          br.nextSibling.textContent === '')
      if (atEnd) br.after(document.createElement('br'))

      sel.removeAllRanges()
      sel.addRange(range)

      onChange(readText(divRef.current))
    }

    function handleInput() {
      if (divRef.current) onChange(readText(divRef.current))
    }

    // Pasted/dropped photos and videos must go through the S3 upload pipeline.
    // Left to the browser, it inserts a local-only <img> that LOOKS fine but
    // never uploads, so it silently vanishes on save + reload.
    function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
      const files = mediaFilesFrom(e.clipboardData.files)
      if (files.length) {
        e.preventDefault()
        onMediaFiles(files)
      }
    }

    function handleDrop(e: React.DragEvent<HTMLDivElement>) {
      const files = mediaFilesFrom(e.dataTransfer.files)
      if (files.length) {
        e.preventDefault()
        onMediaFiles(files)
      }
    }

    function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
      if (e.dataTransfer.types.includes('Files')) e.preventDefault()
    }

    // Media widgets are raw HTML inside the contentEditable, so clicks are
    // handled by delegation rather than React props
    function handleClick(e: React.MouseEvent<HTMLDivElement>) {
      const target = (e.target as HTMLElement).closest<HTMLElement>(
        '[data-media-action]',
      )
      if (!target || !divRef.current?.contains(target)) return
      e.preventDefault()
      const key = target.dataset.mediaKey ?? ''
      const widget = target.closest<HTMLElement>('[data-media-token]')
      const action = target.dataset.mediaAction

      if (action === 'expand') {
        onExpandMedia(key)
      } else if (action === 'reveal' && widget) {
        const url = mediaUrls[key]
        if (url) revealVideo(widget, url)
      } else if (action === 'collapse' && widget) {
        collapseVideo(widget, mediaUrls)
      } else if (action === 'remove' && widget) {
        widget.remove()
        onChange(readText(divRef.current))
      }
    }

    // ── Author legend ──────────────────────────────────────────────────────────
    const authorCounts = new Map<string, number>()
    for (const seg of displaySegments) {
      if (seg.author) {
        authorCounts.set(
          seg.author,
          (authorCounts.get(seg.author) ?? 0) + seg.text.length,
        )
      }
    }
    const authors = [...authorCounts.entries()].sort((a, b) => b[1] - a[1])

    return (
      <div>
        <div
          ref={divRef}
          contentEditable
          suppressContentEditableWarning
          onKeyDown={handleKeyDown}
          onCompositionEnd={handleInput}
          onInput={handleInput}
          onClick={handleClick}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          style={{
            minHeight: '400px',
            padding: '14px 16px',
            fontSize: '18px',
            lineHeight: 1.75,
            border: `2px solid ${GOVUK_COLOURS.midGrey}`,
            borderRadius: '2px',
            outline: 'none',
            wordBreak: 'break-word',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
            width: '100%',
            whiteSpace: 'pre-wrap',
          }}
        />

        {authors.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: '14px',
              flexWrap: 'wrap',
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: `1px solid ${GOVUK_COLOURS.midGrey}`,
            }}
          >
            {authors.map(([author]) => {
              const colour = getUserColour(author, users, currentUser)
              return (
                <div
                  key={author}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: colour,
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: colour, fontWeight: 600 }}>
                    {author}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  },
)
